import {t} from "@lingui/core/macro";
import * as React from "react";

import {TextField} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import type {Typography} from "@khanacademy/wonder-blocks-typography";

import {PASSWORD_MIN_LENGTH} from "./password-util.ts";

const invalidPassword = (minLength: number): string =>
    t`Your password must be at least ${minLength} characters long.`;

interface Props {
    /**
     * Provide a label for the field.
     */
    label: string | React.ReactElement<React.ComponentProps<Typography>>;
    /**
     * Provide a description for the field.
     */
    description?: string | React.ReactElement<React.ComponentProps<Typography>>;
    /**
     * Called when the value has changed.
     */
    onChange: (newValue: string) => unknown;
    /**
     * Called when the field input is validated.
     */
    onValidate?: (errorMessage?: string | null | undefined) => unknown;
    /**
     * The initial input value.
     */
    initialValue?: string;
    /**
     * Provide a placeholder for the input field.
     */
    placeholder?: string;
    /**
     * Provide a different empty error message than the default.
     */
    emptyMessage?: string;
    /**
     * Disable the input field.
     */
    disabled?: boolean;
    /**
     * Optional test id for e2e testing.
     * The testId for the internal input element will
     * have "-field" automatically appended.
     */
    testId?: string;
    /**
     * An optional unique identifier for the TextField.
     * If no id is specified, a unique id will be auto-generated.
     */
    id?: string;
    required?: boolean | string;
}

export const PasswordTextField = React.forwardRef<HTMLInputElement, Props>(
    (
        {
            label,
            description,
            onValidate,
            placeholder,
            disabled,
            testId,
            id,
            required,
            initialValue = "",
            emptyMessage = "",
            onChange,
        },
        ref,
    ) => {
        const [value, setValue] = React.useState(initialValue);
        const [errorMessage, setErrorMessage] = React.useState<
            string | null | undefined
        >(null);

        const handleChange = React.useCallback(
            (newValue: string) => {
                setValue(newValue);
                onChange(newValue);
            },
            [onChange],
        );

        const validate = React.useCallback(
            (value: string) => {
                if (!value) {
                    return emptyMessage || t`Please create a password.`;
                }
                if (value.length < PASSWORD_MIN_LENGTH) {
                    return invalidPassword(PASSWORD_MIN_LENGTH);
                }
            },
            [emptyMessage],
        );

        const handleValidate = React.useCallback(
            (errorMessage?: string | null | undefined) => {
                setErrorMessage(errorMessage);
                if (onValidate) {
                    onValidate(errorMessage);
                }
            },
            [onValidate],
        );

        return (
            <LabeledField
                id={id}
                testId={testId}
                contextLabel={required ? t`required` : undefined}
                errorMessage={errorMessage}
                field={
                    <TextField
                        type="password"
                        value={value}
                        placeholder={placeholder}
                        onChange={handleChange}
                        validate={validate}
                        onValidate={handleValidate}
                        disabled={disabled}
                        ref={ref}
                        required={required}
                    />
                }
                label={label}
                description={description}
            />
        );
    },
);
